/* voyado-consent.js
 * Consent gate for Voyado Elevate — additive, no call-site changes.
 *
 * We do NOT skip requests (the collection/search grids and recommendations all
 * depend on them). Instead we control the KEYS the SDK attaches to every
 * request by wrapping its session handler:
 *
 *   - analytics consent granted → the SDK's persisted voyado.session keys
 *     (stable identity, personalisation + attribution work)
 *   - no consent / undecided / API not ready → fresh random keys generated PER
 *     request, so a non-consenting visitor carries no stable tracking identity.
 *
 * We also force notify=false onto every query.* call in the withheld case, so
 * those requests perform no behavioural registration (Elevate's no-consent
 * guidance): random keys AND notify=false together.
 *
 * This works because the SDK's createBaseUrl() reads `config.session` live on
 * every query.* / notify.* request, and window.elevate.query.c.session /
 * notify.c.session are mutable, and the query methods take a (request, body)
 * pair whose request arg maps to the query-string params. So the call sites in
 * the other voyado-*.js files stay exactly as they are.
 */
(function () {
  // Other voyado-*.js files await this instead of checking `window.elevate`
  // once — the embed script assigns it asynchronously, independently of theme
  // script load order, so a single check-and-give-up races it (that race is
  // what made collection/search/recs grids intermittently need a page reload
  // to appear). Resolves the instant installInto() below runs. Belt-and-
  // suspenders timeout: if the embed never shows up at all (blocked, disabled),
  // resolve anyway after a generous wait so dependents can give up gracefully
  // instead of awaiting forever.
  var _elevateReadyResolve;
  window.voyadoElevateReady = new Promise(function (resolve) { _elevateReadyResolve = resolve; });
  setTimeout(function () { _elevateReadyResolve(window.elevate); }, 8000);

  // --- Shopify Customer Privacy API: its methods return undefined until the
  // consent-tracking-api feature is loaded via loadFeatures, so trigger it.
  var cpReady;
  function loadCustomerPrivacy() {
    if (cpReady) return cpReady;
    cpReady = new Promise(function (resolve) {
      var done = false;
      function finish() { if (!done) { done = true; resolve(); } }
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && cp.currentVisitorConsent && cp.currentVisitorConsent() !== undefined) return finish();
        var lf = window.Shopify && window.Shopify.loadFeatures;
        if (typeof lf !== 'function') return finish();
        lf([{ name: 'consent-tracking-api', version: '0.1' }], finish);
        setTimeout(finish, 1500); // callback isn't always invoked in async mode
      } catch (e) { finish(); }
    });
    return cpReady;
  }
  loadCustomerPrivacy();

  // We attach the SDK's STABLE persisted keys ONLY when analytics consent is
  // AFFIRMATIVELY granted. Everything else — declined, undecided, or not-yet-known
  // — gets fresh random keys per request.
  //
  // Consent is only knowable asynchronously: currentVisitorConsent() returns
  // undefined until loadFeatures resolves, and Shopify's consent state isn't
  // readable here (some of it lives in HTTP-only cookies Shopify tells you never
  // to parse). So we must GUESS for the window before the API loads. We guess
  // "withheld": until consent is confirmed 'yes', no request carries a stable
  // identity. This closes the gap where the recs grids/sliders fire their first
  // wave of query.* calls the instant window.elevate appears — before the consent
  // API has resolved — which previously leaked persisted keys for a declining
  // visitor. The tradeoff: a consenting visitor's earliest requests are anonymous
  // too, then snap to their persisted keys the moment consent resolves.
  function consentWithheld() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    var v = cp && cp.currentVisitorConsent && cp.currentVisitorConsent();
    return !(v && (v.analytics === 'yes' || v.analytics === true));
  }

  function randomKeys() {
    return { customerKey: crypto.randomUUID(), sessionKey: crypto.randomUUID() };
  }

  // Wrap one config object's session handler. Idempotent.
  function gate(cfg) {
    if (!cfg || typeof cfg.session !== 'function' || cfg.session.__voyadoGated) return;
    var orig = cfg.session;
    function gated() {
      return consentWithheld() ? randomKeys() : orig();
    }
    gated.__voyadoGated = true;
    // Preserve the localStorageBackedSession helpers in case anything uses them.
    if (typeof orig.reset === 'function') gated.reset = orig.reset.bind(orig);
    if (typeof orig.updateCustomerKey === 'function') gated.updateCustomerKey = orig.updateCustomerKey.bind(orig);
    cfg.session = gated;
  }

  // createBaseUrl() reads `this.__config.session` per request; the SDK exposes
  // that config as `.c`. Wrap BOTH names on query AND notify so we hit whichever
  // object the request path actually reads — covers the build's property naming.
  function gateInstance(inst) {
    if (!inst) return;
    gate(inst.c);
    gate(inst.__config);
  }

  // `notify` is a query-string param (like pageReference/customerKey), and in the
  // SDK the query methods take (request, body) where the first arg maps to those
  // query-string params and the second is the POST body. So when consent is
  // withheld we force notify=false into the request arg on every query — the
  // query then performs NO behavioural registration (Elevate's guidance for the
  // no-consent / legacy model).
  //
  // CRITICAL: consent is only knowable asynchronously, but the recs grids/sliders
  // fire their query.* calls the instant window.elevate appears — usually BEFORE
  // the consent API has resolved. If we decided synchronously here, every one of
  // those early calls would read consent as "not yet known" → withheld → random
  // keys + notify=false, even for a consenting visitor. So we AWAIT the consent
  // API first, then decide with the real state. This also fixes the session keys:
  // the SDK reads config.session() while building the URL INSIDE fn.call(), which
  // now runs only after consent has resolved. loadCustomerPrivacy() resolves
  // immediately when consent is already available (the common case → no delay);
  // it only waits during the genuine pre-resolution window.
  var QUERY_METHODS = ['landingPage', 'productPage', 'searchPage', 'cartPage', 'autocomplete'];
  function gateQueryMethods(q) {
    if (!q) return;
    QUERY_METHODS.forEach(function (name) {
      var fn = q[name];
      if (typeof fn !== 'function' || fn.__voyadoNotifyGated) return;
      async function gatedQuery(request, body) {
        await loadCustomerPrivacy();
        var req = consentWithheld()
          ? Object.assign({}, request, { notify: false })
          : request;
        return fn.call(this, req, body);
      }
      gatedQuery.__voyadoNotifyGated = true;
      q[name] = gatedQuery;
    });
  }

  function installInto(e) {
    if (!e) return;
    gateInstance(e.query);
    gateInstance(e.notify);
    gateQueryMethods(e.query);
    _elevateReadyResolve(e);
  }

  // The race that matters: the recommendation/grid components fire their query.*
  // calls the instant window.elevate appears — possibly before a polling gate
  // would wrap. So wrap SYNCHRONOUSLY at the moment the embed assigns
  // window.elevate (property trap), which runs before any consumer can read it.
  if (window.elevate) {
    installInto(window.elevate); // embed already loaded
  } else {
    var _elevate;
    var trapped = false;
    try {
      Object.defineProperty(window, 'elevate', {
        configurable: true,
        enumerable: true,
        get: function () { return _elevate; },
        set: function (v) { _elevate = v; installInto(v); },
      });
      trapped = true;
    } catch (e) { /* fall through to poll */ }

    // Belt-and-suspenders: if the trap couldn't be set (or the embed redefines
    // the property over ours), poll briefly so we still wrap before idle clicks.
    if (!trapped) {
      var tries = 0;
      var id = setInterval(function () {
        if (window.elevate) { installInto(window.elevate); clearInterval(id); }
        else if (++tries >= 100) clearInterval(id);
      }, 30);
    }
  }
})();
